/**
 * ArchivedActivitiesScreen — adaptateur primaire de la route `archived-activites` en mode parent.
 *
 * Fil : `useArchivedActivitiesData` (les pages) + `archivedActivitiesViewModel` (la liste) +
 * `useArchivedActivitiesState` (sélection, restauration) → `<ArchivedActivitiesPage>`.
 *
 * La fiche d'une activité est celle du design system (`useActivityDetailsFlow`, entrée « archives »).
 */
import React from "react"
import { useSelector } from "react-redux"

import { ArchivedActivitiesPage, ConfirmDialog } from "../../../../../../design-system"
import { type ApiActivity } from "../../../../adapters/parent/Activities/activityAdapter"
import { useActivityDetailsFlow } from "../../../hooks/useActivityDetailsFlow"
import { archivedActivitiesViewModel } from "../../../../view-models/parent/Activities/archivedActivitiesViewModel"
import { ActivityOverlays } from "./ActivityOverlays"
import { useArchivedActivitiesData } from "./useArchivedActivitiesData"
import { useArchivedActivitiesState } from "./useArchivedActivitiesState"

export type ArchivedActivitiesScreenProps = {
    route?: { params?: { studentId?: string | number } | null } | null
}

export function ArchivedActivitiesScreen(props: ArchivedActivitiesScreenProps) {
    const { studentId, raw, refresh, loadMore, hasMore } = useArchivedActivitiesData(props.route?.params?.studentId)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vm = useSelector((state: any) => archivedActivitiesViewModel(state, raw))
    const details = useActivityDetailsFlow({ studentId, onChanged: refresh })
    const openDetails = (activity: ApiActivity) => {
        if (activity.id != null) details.open(activity.id, { fromArchived: true })
    }
    const { selection, restoreConfirm, restoring, snackbar, actions } = useArchivedActivitiesState({
        studentId,
        activities: vm.activities,
        refresh,
        raw,
        onOpenActivity: openDetails,
    })

    return (
        <>
            <ArchivedActivitiesPage
                activities={vm.activities}
                loading={vm.loading}
                refreshing={vm.refreshing}
                loadingMore={vm.loadingMore}
                hasMore={hasMore}
                error={vm.error}
                onRetry={refresh}
                onRefresh={refresh}
                onLoadMore={loadMore}
                onClose={actions.close}
                onOpenActivity={actions.openActivity}
                selectionMode={selection.selectionMode}
                selectedIds={selection.selectedIds}
                onStartSelection={actions.startSelection}
                onToggleSelect={actions.toggleSelect}
                onToggleSelectAll={actions.toggleSelectAll}
                onCancelSelection={actions.cancelSelection}
                onRestoreSelected={actions.askRestore}
                restoring={restoring}
            />
            <ConfirmDialog
                visible={restoreConfirm != null}
                title={restoreConfirm?.title ?? ""}
                message={restoreConfirm?.message}
                confirmLabel="Restaurer"
                cancelLabel="Annuler"
                onConfirm={actions.confirmRestore}
                onCancel={actions.cancelRestore}
            />
            <ActivityOverlays
                details={details.details}
                notice={details.notice}
                screenNotice={{
                    visible: snackbar != null,
                    message: snackbar?.message ?? "",
                    tone: snackbar?.tone,
                    onDismiss: actions.dismissSnackbar,
                }}
            />
        </>
    )
}

export default ArchivedActivitiesScreen
